/**
 * AudioSpecEditor — edit a single audio clip's spec (speakers / turns / transcript),
 * persist it to the lesson document, then generate/regenerate audio.
 *
 * Layout: Speakers → Dialogue turns → vocal-cue reference → Generate → player → compiled transcript.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLessonContext } from '../../LessonContext';
import { courseRunsService, getPresignedUrl } from '../../../../services/courseRunsService';

interface Speaker { label: string; gender?: string; voice_hint?: string; voice_id?: string }
interface Turn { speaker: string; text: string }

// Fish's official Default Voices (visible in the Fish dashboard, so they can be auditioned there).
const FISH_VOICE_CATALOG: Array<{ id: string; name: string }> = [
  { id: '', name: 'Auto (match gender)' },
  { id: '933563129e564b19a115bedd57b7406a', name: 'Sarah — engaged (F)' },
  { id: 'b347db033a6549378b48d00acb0d06cd', name: 'Selene — meditative (F)' },
  { id: '9a9cf47702da476aa4629e2506d4a857', name: 'Hannah — conversational (F)' },
  { id: 'e3cd384158934cc9a01029cd7d278634', name: 'Laura — confident narrator (F)' },
  { id: '536d3a5e000945adb7038665781a4aca', name: 'Ethan — curious explainer (M)' },
  { id: 'bf322df2096a46f18c579d0baa36f41d', name: 'Adrian — steady narrator (M)' },
  { id: '79d0bd3e4e5444b18f7b6d89b5927bf1', name: 'Jordan — motivational (M)' },
];

// Fish s2-pro inline expression cues (square brackets, placed before the text they affect).
const VOCAL_CUES: Array<{ group: string; cues: string[] }> = [
  { group: 'Emotion', cues: ['[happy]', '[sad]', '[angry]', '[excited]', '[calm]', '[nervous]', '[confident]', '[surprised]', '[sarcastic]', '[curious]', '[empathetic]', '[frustrated]'] },
  { group: 'Tone', cues: ['[whispering]', '[shouting]', '[soft tone]', '[in a hurry tone]'] },
  { group: 'Effects', cues: ['[laughing]', '[chuckling]', '[sighing]', '[gasping]', '[yawning]'] },
  { group: 'Pauses', cues: ['[break]', '[long-break]'] },
];

// Strip inline [cue] expression markers — they drive Fish synthesis but must not appear
// in the published transcript.
function stripVocalCues(text: string): string {
  return (text || '').replace(/\[[^\]]*\]/g, '').replace(/[ \t]{2,}/g, ' ').replace(/ +([.,!?;:])/g, '$1').trim();
}

function audioIndex(audioId: string): number {
  const m = /^aud_(\d+)$/.exec(audioId);
  return m ? parseInt(m[1], 10) : -1;
}

function locateAudio(lessonContent: any, audioId: string): { sIdx: number; cIdx: number; item: any } | null {
  const target = audioIndex(audioId);
  let count = 0;
  const sections = lessonContent?.sections || [];
  for (let s = 0; s < sections.length; s++) {
    const content = sections[s].content || [];
    for (let c = 0; c < content.length; c++) {
      if (content[c]?.type === 'audio') {
        if (count === target) return { sIdx: s, cIdx: c, item: content[c] };
        count += 1;
      }
    }
  }
  return null;
}

function AudioPlayer({ s3Key, version }: { s3Key: string; version: number }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The S3 key is stable across regenerations (the object is overwritten), so we
  // refetch a fresh presigned URL on each new `version` to bypass browser caching.
  useEffect(() => {
    let active = true;
    setUrl(null);
    setError(null);
    getPresignedUrl(s3Key)
      .then((u) => { if (active) setUrl(u); })
      .catch((e: unknown) => { if (active) setError(e instanceof Error ? e.message : 'Failed to load audio'); });
    return () => { active = false; };
  }, [s3Key, version]);
  if (error) return <div className="text-xs text-red-500">{error}</div>;
  if (!url) return <div className="text-xs text-gray-400">Loading audio...</div>;
  // key={url} remounts the element so the browser loads the fresh audio.
  return <audio key={url} controls className="w-full mt-1" preload="metadata" src={url} />;
}

interface AudioSpecEditorProps {
  audioId: string;
  provider: 'openai' | 'fish';
  generatedAudio: any | null;
  generating: boolean;
  onGenerate: (audioId: string) => Promise<void> | void;
}

export function AudioSpecEditor({ audioId, provider, generatedAudio, generating, onGenerate }: AudioSpecEditorProps) {
  const { document, lessonRun, refreshDocument } = useLessonContext();
  const lessonContent: any = document?.lesson_content;
  const located = locateAudio(lessonContent, audioId);
  const item: any = located?.item || {};

  const isDialogue = (item.audio_type || 'narration') === 'dialogue';
  const [speakers, setSpeakers] = useState<Speaker[]>(() => (item.speakers || []).map((s: Speaker) => ({ ...s })));
  const [turns, setTurns] = useState<Turn[]>(() => (item.turns || []).map((t: Turn) => ({ ...t })));
  const [transcript, setTranscript] = useState<string>(item.transcript || '');
  const [narrationVoiceId, setNarrationVoiceId] = useState<string>(item.voice_id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCues, setShowCues] = useState(false);
  const [genVersion, setGenVersion] = useState(0); // bumped after each generate to refresh the player
  // Track the most recently focused text field + caret so cue clicks insert at the cursor.
  const focusRef = useRef<{ kind: 'turn' | 'transcript'; index: number; start: number; end: number } | null>(null);
  const activeElRef = useRef<HTMLTextAreaElement | null>(null);

  // Capture the caret position whenever a text box is focused or the selection moves.
  const captureCaret = (kind: 'turn' | 'transcript', index: number) => (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    activeElRef.current = el;
    focusRef.current = { kind, index, start: el.selectionStart ?? el.value.length, end: el.selectionEnd ?? el.value.length };
  };

  const updateSpeaker = (i: number, patch: Partial<Speaker>) => {
    setSpeakers((prev) => {
      const next = prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
      // Propagate label renames to turns that reference the old label.
      if (patch.label !== undefined && prev[i].label !== patch.label) {
        setTurns((tPrev) => tPrev.map((t) => (t.speaker === prev[i].label ? { ...t, speaker: patch.label as string } : t)));
      }
      return next;
    });
  };
  const addSpeaker = () => setSpeakers((p) => [...p, { label: `Speaker ${p.length + 1}`, gender: 'neutral', voice_hint: '' }]);
  const removeSpeaker = (i: number) => setSpeakers((p) => p.filter((_, idx) => idx !== i));

  const updateTurn = (i: number, patch: Partial<Turn>) => setTurns((p) => p.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const addTurn = () => setTurns((p) => [...p, { speaker: speakers[0]?.label || '', text: '' }]);
  const removeTurn = (i: number) => setTurns((p) => p.filter((_, idx) => idx !== i));

  const insertCue = (cue: string) => {
    const insertText = `${cue} `;
    const tgt = focusRef.current;
    const current = tgt?.kind === 'turn' ? (turns[tgt.index]?.text || '') : transcript;
    const start = tgt ? Math.min(tgt.start, current.length) : current.length;
    const end = tgt ? Math.min(tgt.end, current.length) : current.length;
    const next = current.slice(0, start) + insertText + current.slice(end);
    if (tgt?.kind === 'turn') {
      updateTurn(tgt.index, { text: next });
    } else {
      setTranscript(next);
    }
    // Keep the caret right after the inserted cue so the user can keep editing.
    const caret = start + insertText.length;
    focusRef.current = { kind: tgt?.kind || 'transcript', index: tgt?.index ?? -1, start: caret, end: caret };
    setTimeout(() => {
      const el = activeElRef.current;
      if (el) { el.focus(); el.setSelectionRange(caret, caret); }
    }, 0);
  };

  const buildItem = useCallback(() => {
    if (isDialogue) {
      return { ...item, audio_type: 'dialogue', speakers, turns, transcript: '' };
    }
    return { ...item, transcript, speakers: [], turns: [], voice_id: narrationVoiceId || undefined };
  }, [item, isDialogue, speakers, turns, transcript, narrationVoiceId]);

  const persist = useCallback(async () => {
    if (!located || !lessonRun?.run_id || !lessonContent) throw new Error('Lesson content not loaded');
    const clone = JSON.parse(JSON.stringify(lessonContent));
    clone.sections[located.sIdx].content[located.cIdx] = buildItem();
    await courseRunsService.updateLessonDocument(lessonRun.run_id, { lesson_content: clone });
    await refreshDocument();
  }, [located, lessonRun?.run_id, lessonContent, buildItem, refreshDocument]);

  const handleGenerate = async () => {
    setError(null);
    setSaving(true);
    try {
      await persist();
      await onGenerate(audioId);
      setGenVersion((v) => v + 1);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save or generate');
    } finally {
      setSaving(false);
    }
  };

  if (!located) return <div className="text-xs text-gray-500">Audio spec not found in lesson content.</div>;

  const compiledTranscript = isDialogue
    ? turns.map((t) => `${t.speaker}: ${stripVocalCues(t.text)}`).join('\n')
    : stripVocalCues(transcript);
  const busy = saving || generating;

  return (
    <div className="border rounded p-3 bg-white space-y-4">
      <div className="text-xs text-gray-500">{item.description}</div>

      {isDialogue ? (
        <>
          {/* Speakers */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-bold uppercase text-gray-400">Speakers</div>
              <button type="button" className="text-xs text-blue-600 hover:underline" onClick={addSpeaker}>+ Add speaker</button>
            </div>
            <div className="space-y-1.5">
              {speakers.map((sp, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <input
                    className="border border-gray-300 rounded px-2 py-1 text-xs flex-1 min-w-[6rem]"
                    value={sp.label}
                    placeholder="Name"
                    onChange={(e) => updateSpeaker(i, { label: e.target.value })}
                  />
                  <select
                    className="border border-gray-300 rounded px-1 py-1 text-xs"
                    value={sp.gender || 'neutral'}
                    onChange={(e) => updateSpeaker(i, { gender: e.target.value })}
                  >
                    <option value="female">female</option>
                    <option value="male">male</option>
                    <option value="neutral">neutral</option>
                  </select>
                  {provider === 'fish' && (
                    <select
                      className="border border-gray-300 rounded px-1 py-1 text-xs"
                      value={sp.voice_id || ''}
                      onChange={(e) => updateSpeaker(i, { voice_id: e.target.value })}
                      title="Fish voice"
                    >
                      {FISH_VOICE_CATALOG.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  )}
                  <input
                    className="border border-gray-300 rounded px-2 py-1 text-xs flex-1 min-w-[6rem]"
                    value={sp.voice_hint || ''}
                    placeholder="Voice hint (e.g. confident manager)"
                    onChange={(e) => updateSpeaker(i, { voice_hint: e.target.value })}
                  />
                  <button type="button" className="text-xs text-red-500 hover:underline" onClick={() => removeSpeaker(i)}>✕</button>
                </div>
              ))}
            </div>
          </div>

          {/* Dialogue turns */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-bold uppercase text-gray-400">Dialogue</div>
              <button type="button" className="text-xs text-blue-600 hover:underline" onClick={addTurn}>+ Add turn</button>
            </div>
            <div className="space-y-2">
              {turns.map((t, i) => (
                <div key={i} className="flex items-start gap-2">
                  <select
                    className="border border-gray-300 rounded px-1 py-1 text-xs mt-0.5 w-28 flex-shrink-0"
                    value={t.speaker}
                    onChange={(e) => updateTurn(i, { speaker: e.target.value })}
                  >
                    {speakers.map((sp, idx) => <option key={idx} value={sp.label}>{sp.label}</option>)}
                    {!speakers.some((sp) => sp.label === t.speaker) && <option value={t.speaker}>{t.speaker || '(unknown)'}</option>}
                  </select>
                  <textarea
                    className="border border-gray-300 rounded px-2 py-1 text-xs flex-1 min-h-[2.5rem]"
                    value={t.text}
                    onFocus={captureCaret('turn', i)}
                    onSelect={captureCaret('turn', i)}
                    onClick={captureCaret('turn', i)}
                    onKeyUp={captureCaret('turn', i)}
                    onChange={(e) => updateTurn(i, { text: e.target.value })}
                  />
                  <button type="button" className="text-xs text-red-500 hover:underline mt-0.5" onClick={() => removeTurn(i)}>✕</button>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] font-bold uppercase text-gray-400">Transcript</div>
            {provider === 'fish' && (
              <label className="flex items-center gap-1 text-[10px] text-gray-500">
                Voice
                <select
                  className="border border-gray-300 rounded px-1 py-0.5 text-xs"
                  value={narrationVoiceId}
                  onChange={(e) => setNarrationVoiceId(e.target.value)}
                >
                  {FISH_VOICE_CATALOG.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </label>
            )}
          </div>
          <textarea
            className="border border-gray-300 rounded px-2 py-1 text-xs w-full min-h-[5rem]"
            value={transcript}
            onFocus={captureCaret('transcript', -1)}
            onSelect={captureCaret('transcript', -1)}
            onClick={captureCaret('transcript', -1)}
            onKeyUp={captureCaret('transcript', -1)}
            onChange={(e) => setTranscript(e.target.value)}
          />
        </div>
      )}

      {/* Vocal cue reference (Fish s2-pro) */}
      <div className="border border-gray-200 rounded">
        <button type="button" className="w-full text-left px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 flex items-center justify-between" onClick={() => setShowCues((v) => !v)}>
          <span>Vocal cues {provider === 'fish' ? '(Fish s2-pro — click to insert)' : '(Fish only)'}</span>
          <span>{showCues ? '▲' : '▼'}</span>
        </button>
        {showCues && (
          <div className="px-2 py-2 space-y-2 border-t border-gray-200">
            <div className="text-[10px] text-gray-500">
              Insert inline before the words it should affect, e.g. <code>[whispering] I missed you [break] really.</code>
              {provider !== 'fish' && ' These apply to the Fish engine; OpenAI ignores them.'}
            </div>
            {VOCAL_CUES.map((grp) => (
              <div key={grp.group}>
                <div className="text-[10px] font-bold uppercase text-gray-400">{grp.group}</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {grp.cues.map((c) => (
                    <button key={c} type="button" className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 hover:bg-blue-100 text-gray-700" onClick={() => insertCue(c)}>{c}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="text-xs text-red-600">{error}</div>}

      {/* Generate */}
      <button
        type="button"
        className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        disabled={busy}
        onClick={handleGenerate}
      >
        {saving ? 'Saving...' : generating ? 'Generating...' : generatedAudio ? 'Save & Regenerate' : 'Save & Generate'}
      </button>

      {/* Playback */}
      {generatedAudio?.s3_key && (
        <div>
          <div className="text-[10px] font-bold uppercase text-gray-400 mb-1">Playback</div>
          <AudioPlayer s3Key={generatedAudio.s3_key} version={genVersion} />
          <div className="text-[10px] text-gray-400 mt-1">
            {generatedAudio.provider ? `Provider: ${generatedAudio.provider} | ` : ''}Model: {generatedAudio.model} | {generatedAudio.characters} chars | {(generatedAudio.audio_size_bytes / 1024).toFixed(1)} KB
          </div>
        </div>
      )}

      {/* Compiled transcript preview */}
      <div>
        <div className="text-[10px] font-bold uppercase text-gray-400 mb-1">Transcript (as published)</div>
        <pre className="text-xs text-gray-700 bg-gray-50 rounded p-2 whitespace-pre-wrap font-sans">{compiledTranscript || '(empty)'}</pre>
      </div>
    </div>
  );
}
